import type { Sim } from '@/types'

import { palette } from '@/constants/palette'

const LINE_COUNT = 18
const MAX_POINTS = 420
const STEP_SIZE = 3.5
const CHARGE_RADIUS = 11
const PARTICLES_PER_LINE = 2

type FieldLine = {
    points: Float32Array
    lengths: Float32Array
    count: number
    total: number
}

export const createDipole = (): Sim => {
    let time = 0
    let width = 0
    let height = 0
    let positiveX = 0
    let positiveY = 0
    let negativeX = 0
    let negativeY = 0

    const lines: FieldLine[] = Array.from({ length: LINE_COUNT }, () => ({
        points: new Float32Array(MAX_POINTS * 2),
        lengths: new Float32Array(MAX_POINTS),
        count: 0,
        total: 0
    }))

    const particles = Array.from({ length: LINE_COUNT * PARTICLES_PER_LINE }, (_, index) => ({
        line: index % LINE_COUNT,
        offset: (Math.floor(index / LINE_COUNT) / PARTICLES_PER_LINE) + (index % 5) * 0.07
    }))

    const fieldDirection = (x: number, y: number): [number, number] => {
        const ax = x - positiveX
        const ay = y - positiveY
        const bx = x - negativeX
        const by = y - negativeY
        const ra = Math.pow(ax * ax + ay * ay, 1.5) || 1e-6
        const rb = Math.pow(bx * bx + by * by, 1.5) || 1e-6
        const ex = ax / ra - bx / rb
        const ey = ay / ra - by / rb
        const magnitude = Math.sqrt(ex * ex + ey * ey) || 1e-6

        return [ex / magnitude, ey / magnitude]
    }

    const traceLines = () => {
        const separation = width * (0.17 + 0.035 * Math.sin(time * 0.5))
        const angle = Math.sin(time * 0.23) * 0.32
        const centerX = width / 2
        const centerY = height / 2

        positiveX = centerX - Math.cos(angle) * separation
        positiveY = centerY - Math.sin(angle) * separation
        negativeX = centerX + Math.cos(angle) * separation
        negativeY = centerY + Math.sin(angle) * separation

        const margin = 30

        lines.forEach((line, lineIndex) => {
            const start = (lineIndex / LINE_COUNT) * Math.PI * 2 + angle + 0.08
            let x = positiveX + Math.cos(start) * (CHARGE_RADIUS - 1)
            let y = positiveY + Math.sin(start) * (CHARGE_RADIUS - 1)
            let count = 0
            let total = 0

            line.points[0] = x
            line.points[1] = y
            line.lengths[0] = 0
            count = 1

            while (count < MAX_POINTS) {
                const [dx1, dy1] = fieldDirection(x, y)
                const [dx2, dy2] = fieldDirection(x + dx1 * STEP_SIZE * 0.5, y + dy1 * STEP_SIZE * 0.5)

                x += dx2 * STEP_SIZE
                y += dy2 * STEP_SIZE
                total += STEP_SIZE
                line.points[count * 2] = x
                line.points[count * 2 + 1] = y
                line.lengths[count] = total
                count++

                const toNegativeX = x - negativeX
                const toNegativeY = y - negativeY

                if (toNegativeX * toNegativeX + toNegativeY * toNegativeY < (CHARGE_RADIUS - 2) ** 2) break
                if (x < -margin || y < -margin || x > width + margin || y > height + margin) break
            }

            line.count = count
            line.total = total
        })
    }

    const pointAlong = (line: FieldLine, distance: number): [number, number] => {
        let index = 1

        while (index < line.count - 1 && line.lengths[index] < distance) index++

        const before = line.lengths[index - 1]
        const span = line.lengths[index] - before || 1
        const t = Math.min(1, Math.max(0, (distance - before) / span))
        const x = line.points[(index - 1) * 2] + (line.points[index * 2] - line.points[(index - 1) * 2]) * t
        const y = line.points[(index - 1) * 2 + 1] + (line.points[index * 2 + 1] - line.points[(index - 1) * 2 + 1]) * t

        return [x, y]
    }

    const drawCharge = (ctx: CanvasRenderingContext2D, x: number, y: number, color: string, glowColor: string, positive: boolean) => {
        const glow = ctx.createRadialGradient(x, y, 0, x, y, 38)

        glow.addColorStop(0, glowColor)
        glow.addColorStop(1, 'rgba(255, 255, 255, 0)')
        ctx.fillStyle = glow
        ctx.beginPath()
        ctx.arc(x, y, 38, 0, Math.PI * 2)
        ctx.fill()

        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(x, y, CHARGE_RADIUS, 0, Math.PI * 2)
        ctx.fill()

        ctx.strokeStyle = palette.paper
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(x - 4.5, y)
        ctx.lineTo(x + 4.5, y)

        if (positive) {
            ctx.moveTo(x, y - 4.5)
            ctx.lineTo(x, y + 4.5)
        }

        ctx.stroke()
    }

    return {
        resize: (nextWidth: number, nextHeight: number) => {
            width = nextWidth
            height = nextHeight
            traceLines()
        },

        update: (dt: number) => {
            time += dt
            traceLines()

            particles.forEach(particle => {
                particle.offset = (particle.offset + dt * 0.16) % 1
            })
        },

        draw: (ctx: CanvasRenderingContext2D) => {
            ctx.save()
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'

            const gradient = ctx.createLinearGradient(positiveX, positiveY, negativeX, negativeY)

            gradient.addColorStop(0, 'rgba(255, 91, 46, 0.6)')
            gradient.addColorStop(0.5, 'rgba(150, 156, 180, 0.5)')
            gradient.addColorStop(1, 'rgba(47, 73, 255, 0.6)')

            ctx.strokeStyle = gradient
            ctx.lineWidth = 1

            lines.forEach(line => {
                if (line.count < 2) return

                ctx.beginPath()
                ctx.moveTo(line.points[0], line.points[1])

                for (let index = 1; index < line.count; index++) ctx.lineTo(line.points[index * 2], line.points[index * 2 + 1])

                ctx.stroke()
            })

            ctx.fillStyle = palette.ink
            ctx.strokeStyle = 'rgba(13, 14, 20, 0.35)'
            ctx.lineWidth = 1.6

            particles.forEach(particle => {
                const line = lines[particle.line]

                if (line.count < 2 || line.total < 20) return

                const distance = particle.offset * line.total
                const [x, y] = pointAlong(line, distance)
                const [tailX, tailY] = pointAlong(line, Math.max(0, distance - 14))

                ctx.beginPath()
                ctx.moveTo(tailX, tailY)
                ctx.lineTo(x, y)
                ctx.stroke()

                ctx.beginPath()
                ctx.arc(x, y, 1.9, 0, Math.PI * 2)
                ctx.fill()
            })

            drawCharge(ctx, positiveX, positiveY, palette.signal, 'rgba(255, 91, 46, 0.22)', true)
            drawCharge(ctx, negativeX, negativeY, palette.brand, 'rgba(47, 73, 255, 0.2)', false)

            ctx.restore()
        }
    }
}
